import httpClient from '../httpClient';

// ===== INTERFACES =====

export interface Question {
  id?: number;
  type: 'short_text' | 'paragraph' | 'multiple_choice_single' | 'multiple_choice_multi' | 'star_rating';
  text: string;
  required: boolean;
  options?: string[];
}

export interface FeedbackForm {
  id?: number;
  uuid?: string;
  slug?: string;
  name: string;
  description?: string;
  type: 'feedback' | 'satisfaction' | 'assessment';
  status: 'draft' | 'active' | 'closed';
  expiryDate?: string | null;
  maxResponses?: number | null;
  onePerBrowser: boolean;
  collectName: boolean;
  collectEmail: boolean;
  questions: Question[];
  responseCount?: number;
  questionCount?: number;
  createdAt?: string;
  createdByName?: string;
}

export interface Survey {
  id: string;
  name: string;
  description: string;
  type: string;
  status: 'active' | 'draft' | 'closed';
  responses: number;
  created: string;
  createdBy: string;
  slug?: string;
}

export interface AnalyticsStat {
  label: string;
  value: string;
  subtext: string;
  icon: string;
  color: string;
}

export interface BarChartDataItem {
  label: string;
  value: number;
  height: number;
}

export interface RatingDistributionItem {
  stars: number;
  count: number;
  percentage: number;
  color: string;
}

export interface RecentResponse {
  id: number;
  name: string;
  time: string;
  rating: number;
  comment: string;
}

export interface SurveyOption {
  id: string;
  label: string;
  value: string;
}

export interface FormAnalytics {
  stats: {
    total_responses: number;
    responses_this_week: number;
    first_response: string | null;
    last_response: string | null;
    avg_rating: number;
    completion_rate: number;
  };
  ratingDistribution: Array<{ stars: number; count: number; percentage: number }>;
  avgRatingsByQuestion: Array<{ question_id: number; question_text: string; avg_rating: number; rating_count: number }>;
  recentResponses: Array<{ id: number; respondent_name: string | null; respondent_email: string | null; submitted_at: string; first_rating: number | null; first_comment: string | null }>;
}

// ===== API ENDPOINTS =====
const ENDPOINTS = {
  FORMS: '/admin/feedback/forms',
};

// ===== FORM CRUD FUNCTIONS =====

/**
 * Get all surveys/forms
 */
export const getSurveys = async (): Promise<{
  success: boolean;
  data?: Survey[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<{ success: boolean; data: any[] }>(ENDPOINTS.FORMS);

    // Transform backend response to frontend format
    const surveys: Survey[] = (response.data || []).map((form: any) => ({
      id: String(form.id),
      name: form.name,
      description: form.description || '',
      type: form.type ? form.type.charAt(0).toUpperCase() + form.type.slice(1) : 'Feedback',
      status: form.status,
      responses: form.response_count || 0,
      created: form.created_at ? new Date(form.created_at).toLocaleDateString('en-CA') : '',
      createdBy: form.created_by_name || 'Admin',
      slug: form.slug
    }));

    return { success: true, data: surveys, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch surveys' }
    };
  }
};

/**
 * Get form by ID with questions
 */
export const getFormById = async (id: string): Promise<{
  success: boolean;
  data?: FeedbackForm;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<{ success: boolean; data: any }>(`${ENDPOINTS.FORMS}/${id}`);

    const form = response.data;
    return {
      success: true,
      data: {
        id: form.id,
        uuid: form.uuid,
        slug: form.slug,
        name: form.name,
        description: form.description,
        type: form.type,
        status: form.status,
        expiryDate: form.expiry_date,
        maxResponses: form.max_responses,
        onePerBrowser: form.one_per_browser === 1,
        collectName: form.collect_name === 1,
        collectEmail: form.collect_email === 1,
        questions: (form.questions || []).map((q: any) => ({
          id: q.id,
          type: q.question_type,
          text: q.question_text,
          required: q.is_required === 1,
          options: q.options ? (typeof q.options === 'string' ? JSON.parse(q.options) : q.options) : undefined
        }))
      },
      error: null
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch form' }
    };
  }
};

/**
 * Create new form
 */
export const createForm = async (formData: Omit<FeedbackForm, 'id' | 'uuid' | 'slug'>): Promise<{
  success: boolean;
  data?: { id: number; slug: string; publicUrl: string };
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<{ success: boolean; data: any }>(
      ENDPOINTS.FORMS,
      {
        name: formData.name,
        description: formData.description,
        type: formData.type,
        status: formData.status,
        expiryDate: formData.expiryDate,
        maxResponses: formData.maxResponses,
        onePerBrowser: formData.onePerBrowser,
        collectName: formData.collectName,
        collectEmail: formData.collectEmail,
        questions: formData.questions.map(q => ({
          type: q.type,
          text: q.text,
          required: q.required,
          options: q.options
        }))
      }
    );

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to create form' }
    };
  }
};

/**
 * Update form
 */
export const updateForm = async (id: string, formData: Partial<FeedbackForm>): Promise<{
  success: boolean;
  error?: { message: string } | null;
}> => {
  try {
    await httpClient.put(`${ENDPOINTS.FORMS}/${id}`, {
      name: formData.name,
      description: formData.description,
      type: formData.type,
      status: formData.status,
      expiryDate: formData.expiryDate,
      maxResponses: formData.maxResponses,
      onePerBrowser: formData.onePerBrowser,
      collectName: formData.collectName,
      collectEmail: formData.collectEmail,
      questions: formData.questions?.map(q => ({
        type: q.type,
        text: q.text,
        required: q.required,
        options: q.options
      }))
    });
    return { success: true, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to update form' }
    };
  }
};

/**
 * Delete form
 */
export const deleteForm = async (id: string): Promise<{
  success: boolean;
  error?: { message: string } | null;
}> => {
  try {
    await httpClient.delete(`${ENDPOINTS.FORMS}/${id}`);
    return { success: true, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to delete form' }
    };
  }
};

/**
 * Get form analytics
 */
export const getFormAnalytics = async (formId: string): Promise<{
  success: boolean;
  data?: FormAnalytics;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<{ success: boolean; data: FormAnalytics }>(
      `${ENDPOINTS.FORMS}/${formId}/analytics`
    );
    return { success: true, data: response.data, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch analytics' }
    };
  }
};

// ===== ANALYTICS HELPER FUNCTIONS (Transform backend data to frontend format) =====

export const getAnalyticsStats = async (formId?: string): Promise<{
  success: boolean;
  data?: AnalyticsStat[];
  error?: { message: string } | null;
}> => {
  try {
    if (!formId) {
      // Return empty stats if no form selected
      return {
        success: true,
        data: [
          { label: 'Total Responses', value: '0', subtext: 'Select a form', icon: 'MessageSquare', color: '#3B82F6' },
          { label: 'Completion Rate', value: '-', subtext: 'No data', icon: 'CheckCircle', color: '#10B981' },
          { label: 'Average Rating', value: '-', subtext: 'No data', icon: 'Star', color: '#F59E0B' },
          { label: 'This Week', value: '0', subtext: 'responses', icon: 'TrendingUp', color: '#8B5CF6' },
        ],
        error: null
      };
    }

    const response = await getFormAnalytics(formId);
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch analytics');
    }

    const stats = response.data.stats;
    const analyticsStats: AnalyticsStat[] = [
      { label: 'Total Responses', value: String(stats.total_responses || 0), subtext: `+${stats.responses_this_week || 0} this week`, icon: 'MessageSquare', color: '#3B82F6' },
      { label: 'Completion Rate', value: `${stats.completion_rate || 100}%`, subtext: `${stats.total_responses || 0} submissions`, icon: 'CheckCircle', color: '#10B981' },
      { label: 'Average Rating', value: String(stats.avg_rating || '-'), subtext: stats.avg_rating ? '⭐'.repeat(Math.round(stats.avg_rating)) : 'No ratings', icon: 'Star', color: '#F59E0B' },
      { label: 'This Week', value: String(stats.responses_this_week || 0), subtext: 'new responses', icon: 'TrendingUp', color: '#8B5CF6' },
    ];

    return { success: true, data: analyticsStats, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch analytics stats' }
    };
  }
};

export const getBarChartData = async (formId?: string): Promise<{
  success: boolean;
  data?: BarChartDataItem[];
  error?: { message: string } | null;
}> => {
  try {
    if (!formId) {
      return { success: true, data: [], error: null };
    }

    const response = await getFormAnalytics(formId);
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch analytics');
    }

    const barChartData: BarChartDataItem[] = response.data.avgRatingsByQuestion.map(q => ({
      label: q.question_text.length > 20 ? q.question_text.substring(0, 20) + '...' : q.question_text,
      value: parseFloat(String(q.avg_rating)) || 0,
      height: ((parseFloat(String(q.avg_rating)) || 0) / 5) * 100
    }));

    return { success: true, data: barChartData, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch bar chart data' }
    };
  }
};

export const getRatingDistribution = async (formId?: string): Promise<{
  success: boolean;
  data?: RatingDistributionItem[];
  error?: { message: string } | null;
}> => {
  try {
    if (!formId) {
      return { success: true, data: [], error: null };
    }

    const response = await getFormAnalytics(formId);
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch analytics');
    }

    const colors: Record<number, string> = {
      5: '#F97316',
      4: '#7DD3C0',
      3: '#FFC658',
      2: '#FF8042',
      1: '#FF5858'
    };

    const ratingDistribution: RatingDistributionItem[] = response.data.ratingDistribution.map(r => ({
      stars: r.stars,
      count: r.count,
      percentage: r.percentage,
      color: colors[r.stars] || '#999'
    }));

    return { success: true, data: ratingDistribution, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch rating distribution' }
    };
  }
};

export const getRecentResponses = async (formId?: string): Promise<{
  success: boolean;
  data?: RecentResponse[];
  error?: { message: string } | null;
}> => {
  try {
    if (!formId) {
      return { success: true, data: [], error: null };
    }

    const response = await getFormAnalytics(formId);
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch analytics');
    }

    const recentResponses: RecentResponse[] = response.data.recentResponses.map(r => ({
      id: r.id,
      name: r.respondent_name || 'Anonymous',
      time: r.submitted_at ? formatTimeAgo(new Date(r.submitted_at)) : '',
      rating: r.first_rating || 0,
      comment: r.first_comment || ''
    }));

    return { success: true, data: recentResponses, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch recent responses' }
    };
  }
};

export const getSurveyOptions = async (): Promise<{
  success: boolean;
  data?: SurveyOption[];
  error?: { message: string } | null;
}> => {
  try {
    const surveysResponse = await getSurveys();
    if (!surveysResponse.success || !surveysResponse.data) {
      throw new Error(surveysResponse.error?.message || 'Failed to fetch surveys');
    }

    const surveyOptions: SurveyOption[] = surveysResponse.data
      .filter(s => s.status === 'active')
      .map(s => ({
        id: s.id,
        label: s.name,
        value: s.id
      }));

    return { success: true, data: surveyOptions, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch survey options' }
    };
  }
};

// ===== HELPER FUNCTIONS =====

function formatTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return `${diffMins} minutes ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString();
}
